import React, { useEffect, useState } from 'react';
import md5 from 'md5'

import { Avatar, AvatarTypeMap, Popover, Stack, Typography, useTheme } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { IsEmpty, IsNotEmpty } from '../Helpers';
import OnTrue from './OnTrue';

export interface TZGravatarProps extends Omit<OverridableComponent<AvatarTypeMap<{}, "div">>, 'src'> {
    email: string;
    enableHover?: boolean;
    type?: 'image' | 'avatar';
    size?: number;
    rating?: 'g' | 'pg' | 'r' | 'x';
    defaultImage?: '404' | 'mp' | 'identicon' | 'monsterid' | 'wavatar' | 'retro' | 'robohash' | 'blank' | string;
}

export interface GravatarUserInfo {
    entry?: (EntryEntity)[] | null;
}

export interface EntryEntity {
    id: string;
    hash: string;
    requestHash: string;
    profileUrl: string;
    preferredUsername: string;
    thumbnailUrl: string;
    photos?: (PhotosEntity)[] | null;
    name: Name;
    displayName: string;
    currentLocation: string;
    urls?: (null)[] | null;
}

export interface PhotosEntity {
    value: string;
    type: string;
}
export interface Name {
    givenName: string;
    familyName: string;
    formatted: string;
}

const TZGravatar = ({ email, type = 'avatar', size = 30, rating = 'g', defaultImage = 'mp', enableHover = false, ...rest }: TZGravatarProps) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const imgLink = `https://en.gravatar.com/avatar/${md5(email?.toLowerCase() ?? 'none')}?s=${size}&r=${rating}&d=${defaultImage}`;
    const jsonLink = `https://en.gravatar.com/${md5(email?.toLowerCase() ?? 'none')}.json`;
    const [userInfo, setUserInfo] = useState<GravatarUserInfo>();
    const theme = useTheme();

    const loadData = async () => {
        try {
            var response = await fetch(jsonLink, {
                method: 'get',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Headers': '*',
                    'Access-Control-Allow-Methods': '*',
                }
            }).then(res => res.json()) as GravatarUserInfo;
            setUserInfo(response);
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        if (IsEmpty(email) || !enableHover)
            return;
        loadData();
    }, [email]);

    const handlePopoverOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const popoverOpen = Boolean(anchorEl);

    const GravatarPopover = () => {
        return (
            <OnTrue test={IsNotEmpty(userInfo) && enableHover}>{() =>
                <Popover
                    id="mouse-over-popover"
                    sx={{ pointerEvents: 'none', padding: theme.spacing(1) }}
                    open={popoverOpen}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus>
                    <Stack direction='row' spacing={2}>
                        <img src={userInfo.entry[0].photos[0].value} alt={userInfo.entry[0].name.formatted} />
                        <Stack>
                            <Typography>{userInfo.entry[0].displayName}</Typography>
                            <Typography>{userInfo.entry[0].currentLocation}</Typography>
                        </Stack>
                    </Stack>
                </Popover>}
            </OnTrue>
        );
    }

    if (type === 'avatar')
        return (
            <>
                <Avatar
                    aria-owns={popoverOpen ? 'mouse-over-popover' : undefined}
                    aria-haspopup={IsNotEmpty(userInfo)}
                    onMouseEnter={handlePopoverOpen}
                    onMouseLeave={handlePopoverClose}
                    alt={email}
                    style={{ height: `${size}px`, width: `${size}px` }}
                    src={imgLink}
                    {...rest} />
                <GravatarPopover />
            </>
        );
    else
        return (
            <>
                <img alt={email}
                    src={imgLink}
                    style={{ height: `${size}px`, width: `${size}px` }}
                    onMouseEnter={handlePopoverOpen}
                    onMouseLeave={handlePopoverClose}
                />
                <GravatarPopover />
            </>
        );
};

export default TZGravatar;